/**
 * ⟦A-15⟧ **Les pressions du temps, AVANT qu'elles ne se referment.**
 *
 * Mot de Mathieu, le jour du rendu : *« il n'y a pas vraiment d'ouverture, c'est juste que plus
 * le temps passe, tous ces trucs-là se déclinent. Il est censé y avoir des pop-ups qui montrent
 * quand il y a des trucs qui s'ouvrent, les opportunités, les rivaux qui avancent. »*
 *
 * Le récapitulatif ⟦A-16⟧ couvrait déjà ces mêmes faits — mais **après coup**. Le joueur
 * apprenait qu'une fenêtre s'était refermée sans jamais avoir su qu'elle était ouverte, ce qui
 * est exactement l'inverse du garde-fou de l'avenant : *« prévenir, jamais surprendre »*. Ce
 * fichier est la moitié « prévenir ».
 *
 * ## Ce qu'il ne fait pas
 *
 * **Aucune règle, aucun calcul, aucune constante recopiée.** Les quatre lectures du moteur
 * étaient écrites, testées, et personne ne les appelait : `opportunitesLisibles`
 * (`engine/opportunites.ts`), `rivauxLisibles` et `progressionRival` (`engine/rivaux.ts`),
 * `vaguesRatees` (`engine/trends.ts`). Ce composant ne fait que les rendre. Le seul chiffre
 * qu'il forme lui-même est une **fenêtre de lecture** — « sur le dernier jour » — et elle passe
 * par `H_JOUR` et `progressionRival`, jamais par une soustraction maison.
 *
 * ## L'état neutre par défaut (CLAUDE.md, ⟦A-10⟧)
 *
 * Pas d'offre ouverte ⇒ `FenetresOuvertes` ne rend rien. Pas de PNJ tiré (run d'avant
 * `rivaux.json`, ou contenu qui ne les porte pas) ⇒ `Concurrence` ne rend rien. Ni l'un ni
 * l'autre n'occupe un pixel pour dire qu'il n'a rien à dire.
 *
 * ## ⟦C57 · lot 3-4 · A-174⟧ Le nom, le délai, les chiffres ; les phrases derrière « Les dessous »
 *
 * Une offre : son nom et le temps qui reste (« 2 jours »), plus « Voir l'offre » ; une vague ratée : son
 * nom et UNE phrase. Les rivaux : un nom, un nombre d'abonnés, ce qu'il a pris par jour et l'écart en
 * nombre signé (+ : tu es devant). Les phrases d'avant (« Ils attendent une réponse… », « Une veille, et
 * tu es sur la vague », « tu le devances de 9 900 ») sont reprises MOT POUR MOT derrière le pictogramme 📰.
 */

import {
  H_JOUR,
  PALIERS,
  opportunitesLisibles,
  progressionRival,
  rivaux,
  rivauxLisibles,
  vaguesRatees,
  type GameState,
  type LectureOpportunite,
} from "@maxime/shared";

import { contenu } from "../game/content";
import { ouvert } from "../game/escalier";
import { titreCourt } from "../game/evenementCourt";
import { jourSimule, nombre } from "../game/format";
import { useDecouverteStore } from "../stores/decouverteStore";
import { useRunStore } from "../stores/runStore";
import { useUIStore } from "../stores/uiStore";
import { DessousBloc, DessousLignes, LesDessous } from "./LesDessous";

/**
 * Le temps qui reste, dit comme on le dit à l'oral. `jours_restants` est déjà arrondi vers le
 * bas par le moteur : en dessous d'un jour, on repasse aux heures, sinon toute la dernière
 * journée s'afficherait « 0 jour ».
 */
function delai(l: LectureOpportunite): string {
  if (l.heures_restantes <= 0) return "c'est passé";
  if (l.jours_restants >= 1) {
    return `${nombre(l.jours_restants)} jour${l.jours_restants > 1 ? "s" : ""}`;
  }
  return `${nombre(Math.round(l.heures_restantes))} h`;
}

/**
 * ⟦A-15 §4⟧ **Les fenêtres ouvertes** — collab, sponsor, invitation : ce qui est sur la table
 * *maintenant*, et repartira sans toi.
 *
 * Une opportunité **ne bloque pas** le jeu (c'est tout ce qui la distingue d'un `pending_event`,
 * `engine/events.ts`) : elle attend pendant que le monde tourne. Elle a donc besoin d'un endroit
 * où attendre **visiblement**, sinon la mécanique se résume à une ligne de récapitulatif qui
 * annonce une perte que rien n'avait annoncée.
 *
 * Le bouton rouvre la modale d'événement existante (`EvenementModal`) : mêmes choix, même
 * action `choix_evenement` (⟦C60 · A-177⟧ plus d'avertissement de strike : les strikes sont coupés). On ne crée pas un second système à
 * quelques heures du rendu — le moteur ne l'a pas fait, l'écran non plus.
 */
/**
 * ⟦C62 · lot événements · A-179⟧ **La clé de mémoire d'une vague écartée** : une vague ratée qu'on a fermée ne revient
 * pas, une AUTRE vague, si. Rangée dans la mémoire « déjà vu » du mode découverte (`decouverteStore`), qui est
 * déjà clefée par run et que « Tout réexpliquer » remet à zéro : aucun second stockage.
 */
const cleVague = (trendId: string): string => `vague_ratee:${trendId}`;

export function FenetresOuvertes({ state }: { state: GameState }) {
  const c = contenu();
  const ouvrirOffre = useUIStore((s) => s.ouvrirOffre);
  const pending = useRunStore((s) => s.pending);
  const dejaVues = useDecouverteStore((s) => s.vues);
  const marquerVue = useDecouverteStore((s) => s.marquerVue);
  const offres = opportunitesLisibles(state);
  // ⟦A-15 §2⟧ « La vague qu'on n'a pas prise » : chaude, exploitable, et aucune idée en main ne
  // porte son thème. C'est une invitation à faire une veille, pas une menace — d'où le ton.
  //
  // ⟦C62 · lot événements · A-179⟧ Une alarme qui ne s'éteint jamais s'apprend à ne plus être lue (audit du 21/10) :
  // le bandeau tombait dès la première seconde (« L'IA qui fait peur (ou pas) : aucune de tes idées n'est
  // dessus »), sur une veille et des tendances qui n'existaient pas encore, et il restait au million avec la même
  // phrase. Deux garde-fous : (1) il n'existe qu'une fois la veille ET les tendances ouvertes, c'est-à-dire quand
  // son invitation a un sens ; (2) il se FERME, et une vague fermée ne revient pas (seule une autre le fait).
  const vagueOuverte = ouvert(state, "veille") && ouvert(state, "tendances");
  const ratees = vagueOuverte ? vaguesRatees(state, c).filter((v) => !dejaVues.includes(cleVague(v.trend_id))) : [];

  if (offres.length === 0 && ratees.length === 0) return null;

  return (
    <div className="pressions" role="status">
      {offres.map((o) => {
        const event = c.parEvenement.get(o.event_id);
        return (
          <p
            key={o.event_id}
            className={`pression pression-offre${o.bientot_fermee ? " urgente" : ""}`}
          >
            <span className="pression-icone" aria-hidden="true">
              🎟️
            </span>
            <span className="pression-texte">
              {/* ⟦C57c · A-174⟧ Le titre court à l'écran ; le nom entier ouvre « Les dessous » (comme la fenêtre d'événement). */}
              <strong>{event ? titreCourt(event) : o.event_id}</strong>
              {/* ⟦C57⟧ Le délai, c'est le chiffre qui compte ; la phrase d'avant rouvre derrière 📰. */}
              <span className="pression-detail">
                {o.bientot_fermee ? `Se referme dans ${delai(o)}.` : `Il te reste ${delai(o)}.`}
              </span>
            </span>
            <button
              type="button"
              className="bouton discret pression-bouton"
              disabled={pending}
              onClick={() => ouvrirOffre(o.event_id)}
            >
              Voir l'offre
            </button>
            <LesDessous titre={event?.nom ?? o.event_id} variante="icone" className="pression-dessous">
              <DessousBloc>
                <p className="dessous-texte">
                  {o.bientot_fermee
                    ? `Ça se referme dans ${delai(o)} — après, ils prennent quelqu'un d'autre.`
                    : `Ils attendent une réponse. Tu as ${delai(o)}, jusqu'au jour ${nombre(jourSimule(o.expires_h))}.`}
                </p>
              </DessousBloc>
            </LesDessous>
          </p>
        );
      })}

      {/* Les trois slots peuvent être ratés en même temps : une ligne par vague ferait un mur, et
          un mur ne se lit plus. Elles tiennent sur une seule — c'est la même invitation. */}
      {ratees.length > 0 ? (
        <p className="pression pression-vague">
          <span className="pression-icone" aria-hidden="true">
            🔥
          </span>
          <span className="pression-texte">
            <strong>
              {ratees.map((v) => c.parTendance.get(v.trend_id)?.nom ?? v.trend_id).join(" · ")}
            </strong>
            <span className="pression-detail">Aucune de tes idées n'est dessus.</span>
          </span>
          <LesDessous
            titre={ratees.map((v) => c.parTendance.get(v.trend_id)?.nom ?? v.trend_id).join(" · ")}
            variante="icone"
            className="pression-dessous"
          >
            <DessousBloc>
              <p className="dessous-texte">
                {ratees.map((v) => c.parTendance.get(v.trend_id)?.nom ?? v.trend_id).join(" · ")}
                {ratees.length > 1
                  ? " marchent fort en ce moment, et aucune de tes idées n'est dessus."
                  : " marche fort en ce moment, et aucune de tes idées n'est dessus."}
                {" Une veille, et tu es sur la vague."}
              </p>
            </DessousBloc>
          </LesDessous>
          {/* ⟦C62 · A-179⟧ « Jamais permanent » : un clic l'écarte, et cette vague ne revient pas. */}
          <button
            type="button"
            className="pression-fermer"
            aria-label="Masquer ce bandeau"
            title="Masquer"
            onClick={() => ratees.forEach((v) => marquerVue(cleVague(v.trend_id)))}
          >
            ×
          </button>
        </p>
      ) : null}
    </div>
  );
}

/**
 * ⟦A-15 §3 / M3.16⟧ **La concurrence** — *« la pression la plus forte psychologiquement, et la
 * seule qui donne un visage au temps perdu »*.
 *
 * Il vit sous le compteur d'abonnés, et c'est le seul endroit où il a du sens : la comparaison
 * doit se faire d'un coup d'œil, sans traduction mentale. Une jauge qui descend est une
 * contrainte ; un type qui te double est une histoire.
 *
 * ⚠️ **Les PNJ n'ont aucun effet mécanique** (`engine/rivaux.ts`, et c'est le dessin) : ce
 * panneau ne promet donc rien, il ne menace de rien, il montre. « Ignorer les PNJ reste
 * jouable » (M3.16) reste vrai à l'écran comme dans le moteur.
 *
 * ⟦A-129 · C11⟧ **N'apparaît qu'au premier palier** (`PALIERS[0]`, 100 abonnés) — avant, une
 * chaîne à zéro abonné n'a stricto sensu personne à comparer : le HUD progressif range ce
 * panneau avec les autres éléments qui n'ont rien à dire tant que la partie n'a pas commencé
 * pour de vrai. `rivauxLisibles` peut techniquement rendre des PNJ dès le tour 0 (ils sont
 * seedés au démarrage) ; c'est ce gain d'écran, purement client, qui retient l'affichage.
 */
export function Concurrence({ state }: { state: GameState }) {
  const c = contenu();
  const hudComplet = useUIStore((s) => s.settings.hudComplet);
  const premierPalier = PALIERS[0] ?? 0;
  if (!hudComplet && state.subscribers < premierPalier) return null;
  const lectures = rivauxLisibles(state, c);
  if (lectures.length === 0) return null;

  // Ce qu'ils ont pris **pendant le dernier jour simulé**. La fenêtre est une décision
  // d'affichage ; le nombre, lui, sort de `progressionRival` — la même forme close que le
  // récapitulatif ⟦A-16⟧ utilise, donc les deux écrans ne peuvent pas se contredire.
  const etats = rivaux(state);
  const depuis = Math.max(0, state.clock_h - H_JOUR);

  // ⟦C57⟧ Ce que chaque rival dit, en phrases : réservé à « Les dessous » (et à l'info-bulle de chaque ligne).
  const phrases = lectures.map((r) => {
    const etat = etats.find((e) => e.id === r.id);
    const gagnes = etat ? progressionRival(etat, depuis, state.clock_h) : 0;
    const ecart = r.coude_a_coude
      ? "au coude à coude"
      : r.joueur_devant
        ? `tu le devances de ${nombre(Math.abs(r.ecart))}`
        : `il te devance de ${nombre(r.ecart)}`;
    return { r, gagnes, ecart };
  });

  return (
    <div className="hud-rivaux">
      <p className="hud-rivaux-titre">
        <span>Pendant ce temps, eux</span>
        <LesDessous titre="Pendant ce temps, eux" variante="icone" className="rivaux-dessous">
          <DessousBloc>
            <DessousLignes
              lignes={phrases.map(({ r, gagnes, ecart }) => ({
                icone: r.joueur_devant ? "🙂" : r.coude_a_coude ? "😬" : "😤",
                texte: (
                  <>
                    <strong>{r.nom}</strong> : {nombre(r.subscribers)} abonnés
                    {gagnes > 0 ? ` (+${nombre(gagnes)} par jour)` : ""}, {ecart}.
                    {r.bande !== "connaissance"
                      ? r.bande === "pote"
                        ? " Vous êtes potes : ça s'est construit réponse après réponse."
                        : " C'est un rival : ça s'est construit réponse après réponse."
                      : ""}
                    {r.description ? ` ${r.description}` : ""}
                  </>
                ),
              }))}
            />
          </DessousBloc>
        </LesDessous>
      </p>
      <ul>
        {phrases.map(({ r, gagnes }) => (
          <li key={r.id} className={r.coude_a_coude ? "serre" : ""} title={r.description}>
            <span className="rival-nom">
              {r.joueur_devant ? "🙂" : r.coude_a_coude ? "😬" : "😤"} {r.nom}
            </span>
            <span className="rival-chiffres">
              {nombre(r.subscribers)}
              {gagnes > 0 ? <em> +{nombre(gagnes)}/j</em> : null}
            </span>
            {/* ⟦C57⟧ L'écart en NOMBRE SIGNÉ (+ : tu es devant, − : il te devance), sans phrase. */}
            <span className="rival-ecart">
              {r.coude_a_coude ? "≈ " : ""}
              {r.joueur_devant ? "+" : "−"}
              {nombre(Math.abs(r.ecart))}
            </span>
            {/* ⟦A-84 · SPEC-MOTEUR §7.2bis⟧ La bande relationnelle, quand elle a quitté le
                neutre : « rival » ou « pote » se CONSTRUIT par les réponses aux événements
                (`rival_relation_delta`). La troisième voie — ni l'un ni l'autre — n'affiche
                rien : ignorer les PNJ reste jouable, et ça ne se signale pas. */}
            {r.bande !== "connaissance" ? (
              <span
                className={`rival-bande rival-bande-${r.bande}`}
                title={
                  r.bande === "pote"
                    ? "Vous êtes potes : ça s'est construit réponse après réponse."
                    : "C'est un rival : ça s'est construit réponse après réponse."
                }
              >
                {r.bande === "pote" ? "🤝 pote" : "⚔️ rival"}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
